import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import type {
	BulkMoveRequest,
	BulkMoveResponse,
	CreateNestedFolderRequest,
	Folder,
	MoveFolderRequest,
} from '../types/api';

/**
 * Query keys for nested folder tree operations
 */
export const folderTreeKeys = {
	all: ['folderTree'] as const,
	tree: () => [...folderTreeKeys.all, 'tree'] as const,
	root: () => [...folderTreeKeys.all, 'root'] as const,
	children: (parentId: string) => [...folderTreeKeys.all, 'children', parentId] as const,
	ancestors: (folderId: string) => [...folderTreeKeys.all, 'ancestors', folderId] as const,
	stats: (folderId: string) => [...folderTreeKeys.all, 'stats', folderId] as const,
	cycleCheck: (folderId: string, parentId: string) =>
		[...folderTreeKeys.all, 'cycleCheck', folderId, parentId] as const,
};

/**
 * Hook to fetch the complete folder tree structure
 */
export function useFolderTree() {
	return useQuery({
		queryKey: folderTreeKeys.tree(),
		queryFn: () => api.folders.getFolderTree(),
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 15 * 60 * 1000, // 15 minutes
	});
}

/**
 * Hook to fetch root folders only
 */
export function useRootFolders() {
	return useQuery({
		queryKey: folderTreeKeys.root(),
		queryFn: () => api.folders.getRootFolders(),
		staleTime: 5 * 60 * 1000,
		gcTime: 15 * 60 * 1000,
	});
}

/**
 * Hook to fetch children of a specific folder
 */
export function useFolderChildren(parentId: string) {
	return useQuery({
		queryKey: folderTreeKeys.children(parentId),
		queryFn: () => api.folders.getFolderChildren(parentId),
		enabled: !!parentId,
		staleTime: 5 * 60 * 1000,
		gcTime: 15 * 60 * 1000,
	});
}

/**
 * Hook to fetch ancestors of a folder (for breadcrumbs)
 */
export function useFolderAncestors(folderId: string) {
	return useQuery({
		queryKey: folderTreeKeys.ancestors(folderId),
		queryFn: () => api.folders.getFolderAncestors(folderId),
		enabled: !!folderId,
		staleTime: 10 * 60 * 1000, // Ancestors change less frequently
		gcTime: 30 * 60 * 1000,
	});
}

/**
 * Hook to get enhanced folder statistics
 */
export function useFolderStats(folderId: string) {
	return useQuery({
		queryKey: folderTreeKeys.stats(folderId),
		queryFn: () => api.folders.getFolderStats(folderId),
		enabled: !!folderId,
		staleTime: 2 * 60 * 1000, // Stats can change frequently
		gcTime: 10 * 60 * 1000,
	});
}

/**
 * Hook to check if moving a folder would create a cycle
 */
export function useCycleCheck(folderId: string, parentId: string) {
	return useQuery({
		queryKey: folderTreeKeys.cycleCheck(folderId, parentId),
		queryFn: () => api.folders.wouldCreateCycle(folderId, parentId),
		enabled: !!(folderId && parentId),
		staleTime: 30 * 1000, // Very short cache for safety
		gcTime: 2 * 60 * 1000,
	});
}

/**
 * Hook to create a nested folder
 */
export function useCreateNestedFolder() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateNestedFolderRequest) => api.folders.createNestedFolder(data),
		onMutate: async data => {
			// Cancel any outgoing refetches
			await queryClient.cancelQueries({ queryKey: folderTreeKeys.tree() });
			await queryClient.cancelQueries({ queryKey: folderTreeKeys.root() });
			if (data.parentId) {
				await queryClient.cancelQueries({
					queryKey: folderTreeKeys.children(data.parentId),
				});
			}

			// Snapshot the previous values
			const previousTree = queryClient.getQueryData<Folder[]>(folderTreeKeys.tree());
			const previousRoot = queryClient.getQueryData<Folder[]>(folderTreeKeys.root());
			const previousChildren = data.parentId
				? queryClient.getQueryData<Folder[]>(folderTreeKeys.children(data.parentId))
				: null;

			// Optimistically create a temporary folder
			const tempFolder: Folder = {
				id: `temp-${Date.now()}`,
				name: data.name,
				description: data.description,
				color: data.color,
				sortOrder: data.sortOrder || 0,
				parentId: data.parentId,
				children: [],
				pathArray: data.parentId ? undefined : [data.name],
				flowCount: 0,
				hasChildren: false,
				isRoot: !data.parentId,
				depth: 0,
				fullPath: data.name,
				isEmpty: true,
				createdAt: new Date().toISOString(),
				updatedAt: new Date().toISOString(),
				createdBy: data.createdBy,
			};

			// Optimistically update the relevant caches
			if (data.parentId) {
				// Add to parent's children
				if (previousChildren) {
					queryClient.setQueryData(folderTreeKeys.children(data.parentId), [
						...previousChildren,
						tempFolder,
					]);
				}
			} else {
				// Add to root folders
				if (previousRoot) {
					queryClient.setQueryData(folderTreeKeys.root(), [...previousRoot, tempFolder]);
				}
			}

			return { previousTree, previousRoot, previousChildren, tempFolder };
		},
		onError: (err, data, context) => {
			// Restore previous values on error
			if (context) {
				if (context.previousTree) {
					queryClient.setQueryData(folderTreeKeys.tree(), context.previousTree);
				}
				if (context.previousRoot) {
					queryClient.setQueryData(folderTreeKeys.root(), context.previousRoot);
				}
				if (data.parentId && context.previousChildren) {
					queryClient.setQueryData(
						folderTreeKeys.children(data.parentId),
						context.previousChildren
					);
				}
			}
		},
		onSuccess: (newFolder, data) => {
			// Update the detail cache for the new folder
			queryClient.setQueryData(['folders', 'detail', newFolder.id], newFolder);

			// Update tree cache if it exists
			const currentTree = queryClient.getQueryData<Folder[]>(folderTreeKeys.tree());
			if (currentTree) {
				// For tree updates, we need to rebuild the tree structure
				// For now, invalidate to trigger a refetch
				queryClient.invalidateQueries({ queryKey: folderTreeKeys.tree() });
			}

			// Update children cache
			if (data.parentId) {
				queryClient.setQueryData<Folder[]>(folderTreeKeys.children(data.parentId), old => {
					if (!old) return [newFolder];
					// Replace temp folder with real folder
					return old.map(folder => (folder.id.startsWith('temp-') ? newFolder : folder));
				});
			} else {
				// Update root folders
				queryClient.setQueryData<Folder[]>(folderTreeKeys.root(), old => {
					if (!old) return [newFolder];
					return old.map(folder => (folder.id.startsWith('temp-') ? newFolder : folder));
				});
			}
		},
		onSettled: () => {
			// Invalidate related queries to ensure consistency
			queryClient.invalidateQueries({ queryKey: ['folders'] });
			queryClient.invalidateQueries({ queryKey: ['flows'] }); // Also invalidate flows to refresh folder assignments
		},
	});
}

/**
 * Hook to move a folder to a new parent
 */
export function useMoveFolder() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ folderId, data }: { folderId: string; data: MoveFolderRequest }) =>
			api.folders.moveFolder(folderId, data),
		onMutate: async ({ folderId, data }) => {
			// Cancel relevant queries
			await queryClient.cancelQueries({ queryKey: folderTreeKeys.tree() });
			await queryClient.cancelQueries({ queryKey: folderTreeKeys.ancestors(folderId) });

			// Store previous state for rollback
			const previousTree = queryClient.getQueryData<Folder[]>(folderTreeKeys.tree());
			const previousAncestors = queryClient.getQueryData<Folder[]>(
				folderTreeKeys.ancestors(folderId)
			);

			return { previousTree, previousAncestors };
		},
		onError: (err, variables, context) => {
			// Restore previous state on error
			if (context) {
				if (context.previousTree) {
					queryClient.setQueryData(folderTreeKeys.tree(), context.previousTree);
				}
				if (context.previousAncestors) {
					queryClient.setQueryData(
						folderTreeKeys.ancestors(variables.folderId),
						context.previousAncestors
					);
				}
			}
		},
		onSuccess: (updatedFolder, { folderId }) => {
			// Update the detail cache
			queryClient.setQueryData(['folders', 'detail', folderId], updatedFolder);

			// Invalidate tree-related queries to trigger refetch
			queryClient.invalidateQueries({ queryKey: folderTreeKeys.tree() });
			queryClient.invalidateQueries({ queryKey: folderTreeKeys.root() });
			queryClient.invalidateQueries({ queryKey: folderTreeKeys.ancestors(folderId) });

			// Invalidate children queries for old and new parents
			queryClient.invalidateQueries({
				queryKey: folderTreeKeys.children(updatedFolder.parentId || ''),
			});
		},
	});
}

/**
 * Hook for bulk move operations
 */
export function useBulkMove() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: BulkMoveRequest) => api.folders.bulkMove(data),
		onMutate: async () => {
			// Cancel all tree-related queries
			await queryClient.cancelQueries({ queryKey: folderTreeKeys.all });
		},
		onSuccess: (response: BulkMoveResponse) => {
			// Invalidate all tree-related queries for simplicity
			// In a more optimized version, we could update specific parts of the cache
			queryClient.invalidateQueries({ queryKey: folderTreeKeys.all });
			queryClient.invalidateQueries({ queryKey: ['folders'] });
			queryClient.invalidateQueries({ queryKey: ['flows'] }); // Workflows might have moved too
		},
	});
}

/**
 * Hook to delete a folder with enhanced options
 */
export function useDeleteNestedFolder() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			folderId,
			moveChildrenToRoot = true,
		}: {
			folderId: string;
			moveChildrenToRoot?: boolean;
		}) => api.folders.deleteNestedFolder(folderId, moveChildrenToRoot),
		onMutate: async ({ folderId }) => {
			// Cancel queries
			await queryClient.cancelQueries({ queryKey: folderTreeKeys.tree() });
			await queryClient.cancelQueries({ queryKey: ['folders', 'detail', folderId] });

			// Store previous state
			const previousTree = queryClient.getQueryData<Folder[]>(folderTreeKeys.tree());
			const previousFolder = queryClient.getQueryData<Folder>([
				'folders',
				'detail',
				folderId,
			]);

			// Optimistically remove from cache
			queryClient.removeQueries({ queryKey: ['folders', 'detail', folderId] });

			return { previousTree, previousFolder };
		},
		onError: (err, { folderId }, context) => {
			// Restore on error
			if (context) {
				if (context.previousTree) {
					queryClient.setQueryData(folderTreeKeys.tree(), context.previousTree);
				}
				if (context.previousFolder) {
					queryClient.setQueryData(
						['folders', 'detail', folderId],
						context.previousFolder
					);
				}
			}
		},
		onSuccess: () => {
			// Invalidate all folder-related queries
			queryClient.invalidateQueries({ queryKey: folderTreeKeys.all });
			queryClient.invalidateQueries({ queryKey: ['folders'] });
		},
	});
}

/**
 * Custom hook for managing expanded folder state
 */
export function useExpandedFolders() {
	const EXPANDED_KEY = 'composer.folderTree.expanded';

	const getExpanded = (): Set<string> => {
		try {
			const stored = localStorage.getItem(EXPANDED_KEY);
			return stored ? new Set(JSON.parse(stored)) : new Set();
		} catch {
			return new Set();
		}
	};

	const setExpanded = (expanded: Set<string>) => {
		try {
			localStorage.setItem(EXPANDED_KEY, JSON.stringify([...expanded]));
		} catch {
			// Ignore localStorage errors
		}
	};

	const toggle = (folderId: string) => {
		const current = getExpanded();
		if (current.has(folderId)) {
			current.delete(folderId);
		} else {
			current.add(folderId);
		}
		setExpanded(current);
		return current;
	};

	const isExpanded = (folderId: string) => {
		return getExpanded().has(folderId);
	};

	const expand = (folderId: string) => {
		const current = getExpanded();
		current.add(folderId);
		setExpanded(current);
		return current;
	};

	const collapse = (folderId: string) => {
		const current = getExpanded();
		current.delete(folderId);
		setExpanded(current);
		return current;
	};

	const expandAll = (folderIds: string[]) => {
		const current = new Set([...getExpanded(), ...folderIds]);
		setExpanded(current);
		return current;
	};

	const collapseAll = () => {
		setExpanded(new Set());
		return new Set<string>();
	};

	return {
		getExpanded,
		setExpanded,
		toggle,
		isExpanded,
		expand,
		collapse,
		expandAll,
		collapseAll,
	};
}
